import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { FormLabel, Stack, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';

import MainCard from '@components/ui/cards/MainCard';
import { FormInput } from '@components/FormInput/ui/FormInput';
import {
  PersonalInfoFormFields,
  personalInfoValidationSchema,
} from '@features/settings-personal-info/utils/form-validators';
import { updateUserDataRequest } from '@api/auth/apiService';
import { useAuthSelector } from '@providers/AuthProvider/ui/AuthProvider';
import useToast from '@hooks/useToast';

import UserImageChanger from '@features/settings-personal-info/components/UserImageChanger';
import ChangePhoneDialog from '@features/settings-personal-info/components/ChangePhoneDialog';
import styles from './index.module.scss';

function SettingsPersonalInfoForm() {
  const userData = useAuthSelector((state) => state.userData);
  const renewUserImageLink = useAuthSelector(
    (state) => state.renewUserImageLink
  );
  const successUpdateUserDataAction = useAuthSelector(
    (state) => state.updateUserData
  );
  const { showSuccess } = useToast();
  const [isChangingPhone, setIsChangingPhone] = useState(false);
  const methods = useForm<PersonalInfoFormFields>({
    resolver: yupResolver(personalInfoValidationSchema),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = methods;

  const onSuccessSubmit = (newUserData: any) => {
    if (!userData) {
      return;
    }
    // The phone number is changed only through ChangePhoneDialog, which
    // verifies a code sent to the new number (ZLAYT-1069).
    updateUserDataRequest(userData.id, newUserData, () => {
      successUpdateUserDataAction({ ...userData, ...newUserData });
      showSuccess(
        'Changes saved',
        'Your profile settings changes has been saved.'
      );
    });
  };

  useEffect(() => {
    if (userData) {
      setValue('name', userData.name);
      setValue('email', userData.email);
    }
  }, [setValue, userData]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSuccessSubmit)}>
        <MainCard>
          <Typography variant="h6" gutterBottom>
            Personal info
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Update your photo and personal details here.
          </Typography>
          <Divider sx={{ marginBottom: 3 }} />
          <Stack spacing={3} divider={<Divider />}>
            <Stack direction="row" alignItems="center">
              <FormLabel sx={{ width: '314px' }}>Name</FormLabel>
              <FormInput
                bordered
                type="input"
                placeholder="Enter name"
                className={styles.inputWide}
                {...register('name')}
                errorText={errors.name?.message}
              />
            </Stack>

            <Stack direction="row" alignItems="center">
              <FormLabel sx={{ width: '314px' }}>Email address</FormLabel>
              <FormInput
                bordered
                type="input"
                placeholder="Enter email"
                disabled
                className={styles.inputWide}
                {...register('email')}
                errorText={errors.email?.message}
              />
            </Stack>

            <Stack direction="row" alignItems="center">
              <FormLabel sx={{ width: '314px' }}>Phone</FormLabel>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                className={styles.inputWide}
              >
                <Typography
                  variant="body1"
                  color={userData?.phone ? 'text.primary' : 'text.secondary'}
                >
                  {userData?.phone || 'No phone number yet'}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setIsChangingPhone(true)}
                  aria-label={
                    userData?.phone ? 'Change phone number' : 'Add phone number'
                  }
                >
                  {userData?.phone ? 'Change' : 'Add'}
                </Button>
              </Stack>
            </Stack>

            <Stack direction="row" alignItems="center">
              <FormLabel sx={{ width: '314px' }}>Your photo</FormLabel>
              <UserImageChanger
                imageUrl={userData?.image_url}
                onImageChange={successUpdateUserDataAction}
                onImageError={renewUserImageLink}
              />
            </Stack>

            <Box display="flex" justifyContent="flex-end">
              <Button variant="contained" type="submit">
                Save
              </Button>
            </Box>
          </Stack>
        </MainCard>
      </form>
      <ChangePhoneDialog
        open={isChangingPhone}
        onClose={() => setIsChangingPhone(false)}
        onChanged={(phone) => {
          setIsChangingPhone(false);
          if (userData) {
            successUpdateUserDataAction({ ...userData, phone });
          }
          showSuccess(
            'Phone number updated',
            'Verification codes will now go to your new number.'
          );
        }}
      />
    </FormProvider>
  );
}

export default SettingsPersonalInfoForm;
